// packages/kb-view/src/source-span.ts
//
// LAT-1840 — the source-span fragment grammar (`#L120`, `#L120-L126`, and the
// canonical `120-126` URL-parameter form), in the one place every consumer of
// it reads it from.
//
// It was defined in `apps/web/src/lib/source-span.ts`, next to the in-app
// reader that first needed it. ADR 0058 §10 then made a second consumer
// mandatory: a published page must preserve span fragments through link
// rewriting, and the rewriter (`./reader-links.ts`) lives in this package. A
// package cannot depend on an app, so leaving the grammar in `apps/web` would
// have forced a second copy of it down here.
//
// Two copies of a published grammar is precisely the failure mode ADR 0049 §1
// names when it requires the `/docs` slug grammar be "enforced twice" from one
// definition: enforcing a grammar twice from two regexes is enforcing two
// grammars, and the divergence is silent — a citation that parses in the app
// and is dropped, or re-pointed, on the published page. So the grammar moved
// here, is defined once, and `apps/web/src/lib/source-span.ts` re-exports it.
// Every existing call site is unchanged and now reads this definition.
//
// This is a React-free entry point (`@atmina/kb-view/source-span`), for the
// same reason `./docs-slug` is one: a pure consumer — a Worker router, a
// content gate in `tools/scripts` — must be able to parse a span without
// pulling React into its program.
//
// What deliberately did NOT move: `buildSourceLineWindow` and its
// `SourceLineWindow` type stay in `apps/web`. They are rendering, not grammar
// — clamping an already-parsed range against a specific document and
// projecting a bounded, context-padded line window for the in-app source view.
// No publication path needs them, and this package renders no such view.

/** An inclusive, 1-based line range. `endLine >= startLine` always holds for a
 *  range produced by the parsers below. */
export type SourceLineRange = Readonly<{
  startLine: number;
  endLine: number;
}>;

function toPositiveLine(value: string): number | null {
  const line = Number(value);
  return Number.isSafeInteger(line) && line > 0 ? line : null;
}

/**
 * Parse the fragment form — `#L120` (a single line) or `#L120-L126` (an
 * inclusive range). The leading `#` is optional so a caller holding a bare
 * fragment need not re-attach one. Anchored and total: anything else, an
 * ordinary heading anchor included, is `null` and must be treated as an
 * ordinary fragment rather than a span.
 */
export function parseSourceLineRangeFragment(
  value: string,
): SourceLineRange | null {
  const match = /^#?L([1-9]\d*)(?:-L([1-9]\d*))?$/.exec(value);
  if (!match) return null;
  const startLine = toPositiveLine(match[1]!);
  const endLine = toPositiveLine(match[2] ?? match[1]!);
  if (startLine === null || endLine === null || endLine < startLine) return null;
  return { startLine, endLine };
}

/**
 * Parse the URL-parameter form — the one canonical `start-end` shape that
 * {@link serializeSourceLineRange} emits. Deliberately narrower than the
 * fragment grammar: no `L` prefix, no single-line shorthand, and a non-string
 * input (a repeated or absent parameter) is `null` rather than a throw.
 */
export function parseSourceLineRangeParam(
  value: unknown,
): SourceLineRange | null {
  if (typeof value !== "string") return null;
  const match = /^([1-9]\d*)-([1-9]\d*)$/.exec(value);
  if (!match) return null;
  const startLine = toPositiveLine(match[1]!);
  const endLine = toPositiveLine(match[2]!);
  if (startLine === null || endLine === null || endLine < startLine) return null;
  return { startLine, endLine };
}

/** Emit the one canonical URL-parameter form, `start-end`, for a range —
 *  including when the range is a single line (`12-12`), so the serialized shape
 *  never depends on the range's width. */
export function serializeSourceLineRange(range: SourceLineRange): string {
  return `${range.startLine}-${range.endLine}`;
}

/**
 * Split source text into its lines under the ONE rule every span consumer
 * reads: CR and CRLF normalize to LF, the text splits on LF, and a single
 * trailing empty line (the newline every well-formed text file ends with) is
 * dropped so `lines.length` is the line count a reader would count.
 *
 * It lives here, beside the grammar, for the reason the grammar itself moved
 * here (LAT-1840): a line NUMBER means nothing without the rule that decides
 * where a line ends, and two copies of that rule is two grammars. Three
 * consumers now read it — `buildSourceLineWindow` (the in-app excerpt),
 * `@atmina/evidence`'s span hash, and the attestation read path's line count —
 * and the PRD's A16 ruling only holds if all three count the same way: a
 * "requested range" that is out of range for one and in range for another
 * would decide drift differently on two surfaces.
 *
 * Empty text is zero lines, not one.
 */
export function splitSourceLines(sourceText: string): string[] {
  const normalized = sourceText.replace(/\r\n?/g, "\n");
  if (normalized === "") return [];
  const lines = normalized.split("\n");
  if (lines.at(-1) === "") lines.pop();
  return lines;
}
